/**
 * まなび(5教科クイズ)の問題一覧テーブル。
 *
 * 1行 = 1問。「この問題、子どもが解けるか?」を目で確かめるための並べ方にする:
 * 問題文(ふりがな・絵つき)→ 選択肢(正解に ✓)→ 解説 → skills → 音声の本数。
 *
 * 画像(`prompt.image` / `choices[].image`)は将来入る前提。フィールドが無ければ
 * 何も出さない(空のサムネ枠を並べて一覧を薄くしない)。
 */
import { useState } from 'react';
import { assetUrl } from '../services/env';
import {
  audioCount,
  choiceText,
  questionProblems,
  type BandContent,
  type QuizChoice,
  type QuizQuestion,
  type QuizTextSegment,
} from '../services/quiz';

interface Props {
  readonly questions: readonly QuizQuestion[];
  readonly content: BandContent;
  readonly assetsBase: string | null;
  readonly onRequest: (question: QuizQuestion) => void;
}

/**
 * ふりがな区切り(docs/13 §12)を1行にまとめる。かな版・漢字版のどちらかがあれば出す。
 * 例 `長方形(ちょうほうけい)`。区切りが無ければ `undefined`(何も出さない)。
 */
function segmentSummary(
  ...candidates: readonly (readonly QuizTextSegment[] | undefined)[]
): string | undefined {
  const segments = candidates.find((entry) => entry !== undefined);
  if (segments === undefined) return undefined;
  return segments
    .filter((segment) => segment.r !== undefined)
    .map((segment) => `${segment.t}(${String(segment.r)})`)
    .join(' ');
}

function play(url: string): void {
  const audio = new Audio(url);
  void audio.play().catch(() => undefined);
}

/**
 * 画像。アセットは content と別に生成されるので「パスはあるがファイルが無い」が
 * 起きる。壊れた画像アイコンではなくパス文字列を出して、生成漏れが分かるようにする。
 */
function Picture({ path, base }: { readonly path: string; readonly base: string | null }) {
  const [failed, setFailed] = useState(false);
  const url = assetUrl(path, base);
  if (url === null || failed) return <div className="mono muted">🖼 {path}</div>;
  return (
    <img
      className="thumb"
      src={url}
      alt=""
      loading="lazy"
      title={path}
      onError={() => {
        setFailed(true);
      }}
    />
  );
}

function AudioButton({
  path,
  base,
  label,
}: {
  readonly path: string | undefined;
  readonly base: string | null;
  readonly label: string;
}) {
  const url = assetUrl(path, base);
  if (path === undefined) return null;
  return (
    <button
      className="button button--small"
      type="button"
      disabled={url === null}
      title={url === null ? `音声の配信元が未設定です (${path})` : path}
      onClick={() => {
        if (url) play(url);
      }}
    >
      {label === '' ? '▶' : `▶ ${label}`}
    </button>
  );
}

function ChoiceRow({
  choice,
  index,
  assetsBase,
}: {
  readonly choice: QuizChoice;
  readonly index: number;
  readonly assetsBase: string | null;
}) {
  return (
    <li className={`choice${choice.correct ? ' choice--correct' : ''}`}>
      <span className="choice__mark">{choice.correct ? '✓' : String(index + 1)}</span>
      <span className="choice__text">{choiceText(choice)}</span>
      {choice.textKanji !== undefined && <span className="muted">/ {choice.textKanji}</span>}
      {choice.emoji !== undefined && choice.text !== undefined && (
        <span className="choice__emoji">{choice.emoji}</span>
      )}
      {choice.image !== undefined && <Picture path={choice.image} base={assetsBase} />}
      <AudioButton path={choice.audio} base={assetsBase} label="" />
    </li>
  );
}

export function QuizTable({ questions, content, assetsBase, onRequest }: Props) {
  return (
    <>
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: '84px' }}>問題ID</th>
            <th style={{ width: '150px' }}>単元</th>
            <th>問題文</th>
            <th style={{ width: '220px' }}>選択肢</th>
            <th>解説</th>
            <th style={{ width: '150px' }}>skills</th>
            <th style={{ width: '92px' }}>音声</th>
            <th style={{ width: '92px' }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {questions.map((question) => {
            const unit = content.unitsById.get(question.unit);
            const audio = audioCount(question);
            const problems = questionProblems(question);
            const { prompt } = question;
            return (
              <tr key={question.id}>
                <td className="mono">
                  {question.id}
                  <div className="muted mono">{question.type}</div>
                </td>
                <td>
                  <div>{unit?.title ?? '(単元なし)'}</div>
                  <div className="muted mono">{question.unit}</div>
                </td>
                <td>
                  <div className="quiz-prompt">{prompt.text}</div>
                  {/* 漢字版(「もじの ひょうじ = 漢字」で出る控え。docs/13)。小さく併記する。 */}
                  {prompt.textKanji !== undefined && (
                    <div className="muted">漢字: {prompt.textKanji}</div>
                  )}
                  {prompt.ruby !== undefined && (
                    <div className="muted mono">
                      よみ: {prompt.ruby}
                      {prompt.rubyHidden === true && ' (よみ非表示)'}
                    </div>
                  )}
                  {/*
                   * ふりがなの区切り(docs/13 §12)。実画面では漢字の部分にだけ ruby が乗る。
                   * ここは**切り分けを目で確かめる**ための最小表示なので、
                   * 「漢字(よみ)」の並びを1行に畳んで出すだけにする。
                   * 区切りが無い問題は今までどおり文まるごとの よみ が使われる。
                   */}
                  {segmentSummary(prompt.textSegments, prompt.textKanjiSegments) !== undefined && (
                    <div className="muted mono">
                      区切り: {segmentSummary(prompt.textSegments, prompt.textKanjiSegments)}
                    </div>
                  )}
                  {prompt.emoji !== undefined && <div className="quiz-emoji">{prompt.emoji}</div>}
                  {prompt.image !== undefined && <Picture path={prompt.image} base={assetsBase} />}
                  {problems.length > 0 && (
                    <div className="badge badge--open">{problems.join(' / ')}</div>
                  )}
                </td>
                <td>
                  <ul className="choices">
                    {question.choices.map((choice, index) => (
                      <ChoiceRow
                        key={`${question.id}-${String(index)}`}
                        choice={choice}
                        index={index}
                        assetsBase={assetsBase}
                      />
                    ))}
                  </ul>
                </td>
                <td>
                  {question.explanation ?? <span className="muted">—</span>}
                  {question.explanationKanji !== undefined && (
                    <div className="muted">漢字: {question.explanationKanji}</div>
                  )}
                </td>
                <td>
                  {question.skills.length === 0 ? (
                    <span className="muted">—</span>
                  ) : (
                    question.skills.map((skill) => (
                      <span className="tag" key={skill}>
                        {skill}
                      </span>
                    ))
                  )}
                </td>
                <td>
                  <div className="mono">{String(audio.total)} 本</div>
                  <div className="muted mono">
                    問 {String(audio.prompt)} / 選 {String(audio.choices)}
                  </div>
                  <AudioButton path={prompt.audio} base={assetsBase} label="問題文" />
                </td>
                <td>
                  <button
                    className="button button--small"
                    type="button"
                    onClick={() => {
                      onRequest(question);
                    }}
                  >
                    修正メモ
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {questions.length === 0 && <p className="muted">該当する問題はありません。</p>}
    </>
  );
}
